import React, { useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  Row,
  Cell,
} from "@tanstack/react-table";
// table components
import { v4 as uuidv4 } from "uuid";
import FilterRouter from "./Filters/FilterRouter";
import SortIcons from "./Sorting/SortIcons";

/**
 * A reusable table component built with react-table.
 *
 * This component handles sorting, filtering, and rendering table data dynamically based on provided columns and data.
 *
 * @param columnData  array of objects that have information what the table header should display
 * @param rowData     array of objects that have information what the rows under the table header should display
 */
const Table = ({ columnData, rowData }) => {
  // Initialize React Table
  const table = useReactTable({
    data: rowData,
    columns: columnData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // custom filter
    filterFns: {
      stringTagFilter: (row, columnId, filterValue) => {
        // Show all rows if no filterValue
        if (!filterValue) return true;

        const filters = filterValue
          .split(",")
          .map((f: string) => f.trim())
          .filter((f) => f !== "");
        if (filters.length === 0) return true;

        const cellContent = row.getValue(columnId);

        // check the searchText from labelData (data-search-text attribute)
        if (
          cellContent &&
          typeof cellContent === "object" &&
          "props" in cellContent
        ) {
          const props = cellContent.props as Record<string, unknown>;
          const searchText = props["data-search-text"];
          if (typeof searchText === "string") {
            return filters.some((filter: string) =>
              searchText.toLowerCase().includes(filter.toLowerCase()),
            );
          }
        }

        // check the primitive string
        if (typeof cellContent === "string") {
          return filters.some((filter: string) =>
            cellContent.toLowerCase().includes(filter.toLowerCase()),
          );
        }

        return false;
      },

      includesReactElement: (row, columnId: string, filterValue): boolean => {
        if (!filterValue) return true;
        const cellValue: React.ReactElement = row.getValue(columnId);
        if (columnId === "patientCount") {
          //when filtering patient count
          const intergerValue = String(
            (cellValue.props as { children: React.ReactNode })?.children,
          );
          return intergerValue.includes(filterValue);
        } else {
          //when filtering Mitarbeitenden-ID
          const stringValue = String(
            (cellValue.props as { children: React.ReactNode[] })?.children?.[2],
          );
          return stringValue.toLowerCase().includes(filterValue.toLowerCase());
        }
      },

      // General includes string filter for simple text matching
      includesString: (row, columnId, filterValue) => {
        if (!filterValue) return true;

        const cellValue = row.getValue(columnId);
        const stringValue = String(cellValue);
        return stringValue.toLowerCase().includes(filterValue.toLowerCase());
      },
    },
    // meta (extra informations)
    meta: {
      tableId: uuidv4(),
    },
    columnResizeMode: "onChange", // change column resize mode to "onChange"
  });

  const [currentActiveSortingColumn, setCurrentActiveSortingColumn] =
    useState<number>(-1);

  /**
   * Renders table rows with their visible cells.
   *
   * This function maps over a list of rows and generates JSX elements for each row and its visible cells.
   *
   * @param {Row<unknown>[]} rows - Array of rows to render. Each row contains data and methods for rendering.
   * @returns {JSX.Element[]} - An array of JSX elements representing the rendered rows and cells.
   */
  const renderTableRows = (rows: Row<unknown>[]): React.ReactElement[] => {
    return rows.map((row: Row<unknown>, rowIndex: number) => (
      <tr className="table-row" key={row.id}>
        {row
          .getVisibleCells()
          .map((cell: Cell<unknown, unknown>, cellIndex: number) => {
            const elementsInRow = row.getVisibleCells().length - 1;
            const rowPillStyle =
              cellIndex === 0
                ? " rounded-start " // rounded background at start of row
                : cellIndex === elementsInRow
                  ? " rounded-end " // rounded background at end of row
                  : "";
            const backgroundColor = rowIndex % 2 === 1 ? "WhiteSmoke" : ""; // official HTML color to color odd table rows grey

            return (
              <td
                key={cell.id}
                data-ind={cellIndex}
                data-row-len={elementsInRow}
                className={
                  "table-cell border-0 text-break px-3 " + rowPillStyle
                }
                style={{
                  overflowWrap: "break-word",
                  backgroundColor: backgroundColor,
                  textAlign: "center",
                  verticalAlign: "middle",
                }}
              >
                {cell.renderValue() as React.ReactNode}
              </td>
            );
          })}
      </tr>
    ));
  };

  return (
    <div>
      <div className="d-none d-xl-block">
        <table
          className="table table-borderless table-fixed w-100"
          style={{ tableLayout: "fixed" }}
        >
          <thead>
            {/* header row - show only text value */}
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="table-header">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={
                      "pb-2 pt-3 ps-1 pe-1 " +
                        "fw-bold " +
                        "border-bottom border-1 " +
                        (
                          header.column.columnDef as {
                            customBootstrap?: string;
                          }
                        ).customBootstrap || ""
                    }
                    style={
                      (
                        header.column.columnDef as {
                          customCSS?: React.CSSProperties;
                        }
                      ).customCSS || {}
                    }
                  >
                    <div className="h-100 w-100 d-flex align-items-center justify-content-center">
                      {/* Header text with showing full text on mouse hover */}
                      <span
                        className="text-truncate"
                        data-bs-toggle="tooltip"
                        data-bs-trigger="hover focus"
                        title={String(header.column.columnDef.header) || ""}
                      >
                        {String(header.column.columnDef.header) || ""}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            ))}

            {/* seperated sorting and filter row */}
            <tr className="filter-row" style={{ fontSize: "0.83rem" }}>
              {table.getHeaderGroups()[0].headers.map((header) => (
                <th
                  key={`filter-${header.id}`}
                  className="pb-1 pt-2 ps-1 pe-1 border-bottom"
                  style={
                    (
                      header.column.columnDef as {
                        customCSS?: React.CSSProperties;
                      }
                    ).customCSS || {}
                  }
                >
                  <div className="h-100 w-100 mx-1">
                    {/* sorting and filter div */}
                    <div className="position-relative w-100 h-100 d-flex align-items-center">
                      {/* filter */}
                      <div className="w-100">
                        {header.column.columnDef.enableColumnFilter && (
                          <div className="input-force-full">
                            <FilterRouter
                              table={table}
                              column={header.column}
                            />
                          </div>
                        )}
                      </div>

                      {/* sorting */}
                      {header.column.columnDef.enableSorting && (
                        <div
                          className="position-absolute"
                          style={{
                            right: "-8px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 10,
                            pointerEvents: "auto",
                            backgroundColor: "rgba(255,255,255,0.8)",
                          }}
                        >
                          <SortIcons
                            columnIndex={header.column.getIndex()}
                            header={header}
                            table={table}
                            currentActiveSortingColumn={
                              currentActiveSortingColumn
                            }
                            setCurrentActiveSortingColumn={
                              setCurrentActiveSortingColumn
                            }
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{renderTableRows(table.getRowModel().rows)}</tbody>
        </table>
      </div>
      <p className="text-center py-3 my-3 bg-danger text-white d-xl-none rounded">
        Das Fenster ist zu klein, um die Tabelle anzuzeigen. <br />
        Bitte vergrößern Sie das Fenster oder verwenden Sie ein größeres
        Anzeigegerät.
      </p>
    </div>
  );
};

export default Table;
